import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MainLayout from "../Themes/MainLayout";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import {
  ThemeProvider,
  Box,
  Button,
  Container,
  Paper,
  CssBaseline,
  Typography,
  TextField,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  TableRow,
  TableCell,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Skeleton,
  MenuItem,
  Tabs,
  Tab,
  Popover,
} from "@mui/material";
import {
  CloudUpload,
  Visibility,
  Delete,
  Add,
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import Pagination from "@mui/material/Pagination";
import { styled } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Styled components
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const Antivirus = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingScanId, setEditingScanId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadMonth, setUploadMonth] = useState(dayjs()); // Default to current month
  const [filterMonth, setFilterMonth] = useState(dayjs()); // Default to current month
  const [statusFilter, setStatusFilter] = useState("All");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [remarksDialog, setRemarksDialog] = useState({
    open: false,
    scanId: null,
    remarks: "",
  });
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    image: null,
    title: "",
  });
  const [scanHistory, setScanHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [beforeScanPreview, setBeforeScanPreview] = useState(null);
  const [afterScanPreview, setAfterScanPreview] = useState(null);
  const [afterScan2Preview, setAfterScan2Preview] = useState(null);
  const [beforeScanFile, setBeforeScanFile] = useState(null);
  const [afterScanFile, setAfterScanFile] = useState(null);
  const [afterScan2File, setAfterScan2File] = useState(null);
  const [uploadRemarks, setUploadRemarks] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  
  // Custom hooks
  const { empId, boId, roles } = useCurrentUser();
  const isAdmin = useMemo(() => roles?.includes("Admin"), [roles]);
  const isRejectedReupload = useMemo(
    () => isEditMode && editingScanId !== null &&
      sessionStorage.getItem(`scan_${editingScanId}_original_status`) === "Rejected",
    [isEditMode, editingScanId]
  );
  const canUpload = useMemo(
    () => {
      if (isEditMode) {
        if (isRejectedReupload) {
          // For rejected reupload: only after_scan2 is required
          return !!afterScan2File;
        } else {
          // For normal edit: allow update if at least one file is changed
          return !!(beforeScanFile || afterScanFile);
        }
      } else {
        // In create mode: require both files
        return !!(beforeScanFile && afterScanFile);
      }
    },
    [beforeScanFile, afterScanFile, afterScan2File, isEditMode, isRejectedReupload]
  );

  // Load scan history from API
  const loadScanHistory = useCallback(
    async (filters = {}) => {
      try {
        setHistoryLoading(true);

        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", rowsPerPage);

        // Tab-based filtering
        if (activeTab === 0) {
          // Employee Scan tab - show only current user's scans (for both admin and employees)
          params.append("employee_id", empId);
          params.append("tabType", "employee");
        } else {
          // History & Approval tab - show all scans for admins, own scans for employees
          if (!isAdmin && empId) {
            params.append("employee_id", empId);
          }
          params.append("tabType", "history");
        }

        if (filters.month) params.append("month", filters.month);
        if (filters.status && filters.status !== "All") {
          params.append("status", filters.status);
        }
        if (filters.employeeName) {
          params.append("employeeName", filters.employeeName);
        }

        const res = await fetch(
          `${BASE_URL}/antivirus/scans?${params.toString()}`
        );

        if (!res.ok) throw new Error("Failed to load scans");

        const data = await res.json();
        setScanHistory(data.data || []);
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        console.error(err);
        setError("Unable to load scan history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [page, rowsPerPage, isAdmin, empId, activeTab]
  );

  // Load scan history on mount and filter changes
  useEffect(() => {
    const filters = {};
    if (filterMonth) filters.month = filterMonth.format("YYYY-MM");
    if (statusFilter) filters.status = statusFilter;
    if (employeeFilter && activeTab === 1) {
      filters.employeeName = employeeFilter;
    }

    loadScanHistory(filters);
  }, [filterMonth, statusFilter, employeeFilter, page, loadScanHistory, activeTab]);

  // Reset to page 1 when filters or tab changes
  useEffect(() => {
    setPage(1);
  }, [filterMonth, statusFilter, employeeFilter, activeTab]);

  // File validation with magic byte checking
  const validateFile = useCallback((file) => {
    if (!file) return { isValid: false, error: "No file selected" };

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return {
        isValid: false,
        error: "Invalid file type. Please upload JPEG, PNG, GIF, or WebP images only.",
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: "File size too large. Please upload files smaller than 5MB.",
      };
    }

    return { isValid: true, error: null };
  }, []);

  // Handle file selection
  const handleFileChange = useCallback(
    (e, type) => {
      const file = e.target.files[0];
      if (!file) return;

      const validation = validateFile(file);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }

      // Validate file content using magic bytes
      const reader = new FileReader();
      reader.onload = (event) => {
        const arr = new Uint8Array(event.target.result).subarray(0, 12);
        let isValidImage = false;

        // Check magic bytes for different image formats
        // JPEG: FF D8 FF
        if (arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF) {
          isValidImage = true;
        }
        // PNG: 89 50 4E 47
        else if (arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47) {
          isValidImage = true;
        }
        // GIF: 47 49 46 (GIF87a or GIF89a)
        else if (arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46) {
          isValidImage = true;
        }
        // WebP: RIFF ... WEBP
        else if (arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46 &&
                 arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50) {
          isValidImage = true;
        }

        if (!isValidImage) {
          setError("Invalid image file. The file content does not match a valid image format.");
          return;
        }

        const previewURL = URL.createObjectURL(file);

        if (type === "before") {
          if (beforeScanPreview) URL.revokeObjectURL(beforeScanPreview);
          setBeforeScanPreview(previewURL);
          setBeforeScanFile(file);
        } else if (type === "after") {
          if (afterScanPreview) URL.revokeObjectURL(afterScanPreview);
          setAfterScanPreview(previewURL);
          setAfterScanFile(file);
        } else if (type === "after2") {
          if (afterScan2Preview) URL.revokeObjectURL(afterScan2Preview);
          setAfterScan2Preview(previewURL);
          setAfterScan2File(file);
        }

        setError("");
      };

      reader.onerror = () => {
        setError("Failed to read file. Please try again.");
      };

      reader.readAsArrayBuffer(file);
    },
    [beforeScanPreview, afterScanPreview, afterScan2Preview, validateFile]
  );

  // Reset upload form
  const resetUploadForm = useCallback(() => {
    if (beforeScanPreview && beforeScanPreview.startsWith("blob:")) {
      URL.revokeObjectURL(beforeScanPreview);
    }
    if (afterScanPreview && afterScanPreview.startsWith("blob:")) {
      URL.revokeObjectURL(afterScanPreview);
    }

    setBeforeScanPreview(null);
    setAfterScanPreview(null);
    setBeforeScanFile(null);
    setAfterScanFile(null);
    setUploadRemarks("");
    setUploading(false);
  }, [beforeScanPreview, afterScanPreview]);

  // Submit screenshots
  const handleSubmit = useCallback(async () => {
    if (!canUpload) {
      let errorMsg = "Please upload both before and after scan screenshots";
      if (isEditMode) {
        if (isRejectedReupload) {
          errorMsg = "Please upload After Scan 2 screenshot";
        } else {
          errorMsg = "Please select at least one image to update";
        }
      }
      setError(errorMsg);
      return;
    }

    try {
      setUploading(true);
      setError("");

      if (isRejectedReupload) {
        // PUT /antivirus/scan/:id/reupload — updates after_scan2 on the same record
        const formData = new FormData();
        formData.append("modified_by", empId);
        formData.append("after_scan2", afterScan2File);
        if (uploadRemarks) formData.append("employee_remarks", uploadRemarks);

        const res = await fetch(`${BASE_URL}/antivirus/scan/${editingScanId}/reupload`, {
          method: "PUT",
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Reupload failed");

        setSuccess(data.message || "Scan reuploaded successfully!");
      } else {
        // Normal upload or edit (before_scan / after_scan1)
        const formData = new FormData();
        formData.append("bo_id", boId);
        formData.append("employee_id", empId);
        formData.append("created_by", empId);
        formData.append("modified_by", empId);
        formData.append("scan_month", uploadMonth.format("YYYY-MM"));
        if (uploadRemarks) formData.append("employee_remarks", uploadRemarks);

        if (beforeScanFile instanceof File) formData.append("before_scan", beforeScanFile);
        if (afterScanFile instanceof File) formData.append("after_scan1", afterScanFile);

        const endpoint = isEditMode
          ? `${BASE_URL}/antivirus/scan/${editingScanId}`
          : `${BASE_URL}/antivirus/upload`;
        const method = isEditMode ? "PUT" : "POST";

        const res = await fetch(endpoint, { method, body: formData });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Upload failed");

        // If editing a pending scan, mark it as Reuploaded
        if (isEditMode) {
          try {
            await fetch(`${BASE_URL}/antivirus/update-scan-status`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ scan_id: editingScanId, status: "Reuploaded", remarks: "" }),
            });
          } catch (statusErr) {
            console.error("Error updating status:", statusErr);
          }
        }

        setSuccess(data.message || (isEditMode ? "Scan updated successfully!" : "Screenshots uploaded successfully!"));
      }

      // Close dialog and reset
      setUploadDialogOpen(false);
      resetUploadForm();
      if (afterScan2Preview && afterScan2Preview.startsWith("blob:")) URL.revokeObjectURL(afterScan2Preview);
      setAfterScan2Preview(null);
      setAfterScan2File(null);
      setUploadMonth(dayjs());
      setIsEditMode(false);
      setEditingScanId(null);
      if (editingScanId) sessionStorage.removeItem(`scan_${editingScanId}_original_status`);
      loadScanHistory();
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred");
      setUploading(false);
    }
  }, [
    canUpload,
    isEditMode,
    isRejectedReupload,
    boId,
    empId,
    beforeScanFile,
    afterScanFile,
    afterScan2File,
    afterScan2Preview,
    uploadRemarks,
    loadScanHistory,
    uploadMonth,
    editingScanId,
    resetUploadForm,
  ]);

  // Handle edit scan
  const handleEditScan = useCallback((scan) => {
    setIsEditMode(true);
    setEditingScanId(scan.scan_id);
    setUploadMonth(dayjs(scan.last_scan_date));
    
    // Store the original status for later use
    sessionStorage.setItem(`scan_${scan.scan_id}_original_status`, scan.status);
    
    // Load existing images as previews
    if (scan.has_before_scan) {
      const beforeImageUrl = `${BASE_URL}/antivirus/image/${scan.scan_id}/before_scan`;
      setBeforeScanPreview(beforeImageUrl);
    }
    if (scan.has_after_scan1) {
      const afterImageUrl = `${BASE_URL}/antivirus/image/${scan.scan_id}/after_scan1`;
      setAfterScanPreview(afterImageUrl);
    }
    
    setUploadDialogOpen(true);
  }, []);

  // Handle preview
  const handlePreview = useCallback((scanIdOrUrl, imageTypeOrPreview, title) => {
    // If it's a blob URL (from file upload), use it directly
    if (typeof scanIdOrUrl === "string" && scanIdOrUrl.startsWith("blob:")) {
      setPreviewDialog({
        open: true,
        image: scanIdOrUrl,
        title: imageTypeOrPreview,
      });
    } else if (typeof scanIdOrUrl === "string" && scanIdOrUrl.startsWith("data:")) {
      // Data URL from file upload
      setPreviewDialog({
        open: true,
        image: scanIdOrUrl,
        title: imageTypeOrPreview,
      });
    } else {
      // API endpoint - scanIdOrUrl is scan_id, imageTypeOrPreview is imageType
      const imageUrl = `${BASE_URL}/antivirus/image/${scanIdOrUrl}/${imageTypeOrPreview}`;
      setPreviewDialog({
        open: true,
        image: imageUrl,
        title: title,
      });
    }
  }, []);

  // Handle file removal
  const handleRemoveFile = useCallback(
    (type) => {
      if (type === "before") {
        if (beforeScanPreview && beforeScanPreview.startsWith("blob:")) {
          URL.revokeObjectURL(beforeScanPreview);
        }
        setBeforeScanPreview(null);
        setBeforeScanFile(null);
        // Reset file input to allow re-uploading the same file
        const beforeInput = document.querySelector('input[data-file-type="before"]');
        if (beforeInput) beforeInput.value = "";
      } else if (type === "after") {
        if (afterScanPreview && afterScanPreview.startsWith("blob:")) {
          URL.revokeObjectURL(afterScanPreview);
        }
        setAfterScanPreview(null);
        setAfterScanFile(null);
        // Reset file input to allow re-uploading the same file
        const afterInput = document.querySelector('input[data-file-type="after"]');
        if (afterInput) afterInput.value = "";
      } else if (type === "after2") {
        if (afterScan2Preview && afterScan2Preview.startsWith("blob:")) {
          URL.revokeObjectURL(afterScan2Preview);
        }
        setAfterScan2Preview(null);
        setAfterScan2File(null);
        // Reset file input to allow re-uploading the same file
        const after2Input = document.querySelector('input[data-file-type="after2"]');
        if (after2Input) after2Input.value = "";
      }
    },
    [beforeScanPreview, afterScanPreview, afterScan2Preview]
  );

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (beforeScanPreview && beforeScanPreview.startsWith("blob:")) {
        URL.revokeObjectURL(beforeScanPreview);
      }
      if (afterScanPreview && afterScanPreview.startsWith("blob:")) {
        URL.revokeObjectURL(afterScanPreview);
      }
      if (afterScan2Preview && afterScan2Preview.startsWith("blob:")) {
        URL.revokeObjectURL(afterScan2Preview);
      }
    };
  }, [beforeScanPreview, afterScanPreview, afterScan2Preview]);

  // Format month year
  const formatMonthYear = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      month: "long",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  // Update scan status
  const updateScanStatus = (scanId, status) => {
    if (status === "Rejected") {
      setRemarksDialog({ open: true, scanId, remarks: "" });
    } else {
      sendStatusUpdate(scanId, status, "");
    }
  };

  // Send status update
  const sendStatusUpdate = async (scanId, status, remarks) => {
    try {
      const res = await fetch(`${BASE_URL}/antivirus/update-scan-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scan_id: scanId, status, remarks, approver_employee_id: empId }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update status");
      }

      setSuccess("Status updated successfully!");
      setRemarksDialog({ open: false, scanId: null, remarks: "" });
      loadScanHistory();
    } catch (error) {
      console.error(error);
      setError("Unable to update scan status.");
    }
  };

  // Handle delete
  const handleDelete = (scan) => {
    // Only allow deletion for "Uploaded" status
    if (scan.status !== "Uploaded") {
      setError("Can only delete scans with Uploaded status");
      return;
    }
    setSelectedScan(scan);
    setDeleteId(scan.scan_id);
    setConfirmOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await fetch(`${BASE_URL}/antivirus/scan/${deleteId}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Delete failed");
      }

      setSuccess("Scan deleted successfully");
      const filters = {};
      if (filterMonth) filters.month = filterMonth.format("YYYY-MM");
      await loadScanHistory(filters);
    } catch (err) {
      console.error("Error deleting scan:", err);
      setError(err.message || "Failed to delete scan");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setSelectedScan(null);
    }
  };

  // Remarks popover cell component - self-contained with local anchor state
  const RemarksCell = ({ row }) => {
    const [anchor, setAnchor] = useState(null);
    const hasAny = row.employee_remarks || row.rejection_remarks || row.approval_remarks;
    if (!hasAny) return <Typography variant="body2" color="text.secondary">-</Typography>;

    const entries = [
      { label: "Employee Note", value: row.employee_remarks, color: "#1565c0", bg: "#e3f2fd" },
      { label: "Rejection Reason", value: row.rejection_remarks, color: "#b71c1c", bg: "#ffebee" },
      { label: "Approval Note", value: row.approval_remarks, color: "#1b5e20", bg: "#e8f5e9" },
    ].filter(e => e.value);

    return (
      <>
        <Tooltip title="View remarks">
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ padding: "2px" }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#2DB0E4" }} />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { borderRadius: "10px", p: 1.5, minWidth: 240, maxWidth: 320, boxShadow: 3 } }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555", display: "block", mb: 1 }}>
            Remarks History
          </Typography>
          {entries.map((entry, i) => (
            <Box key={i} sx={{ mb: i < entries.length - 1 ? 1 : 0 }}>
              <Box sx={{
                backgroundColor: entry.bg,
                borderLeft: `3px solid ${entry.color}`,
                borderRadius: "0 6px 6px 0",
                px: 1.5,
                py: 0.75,
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: entry.color, display: "block", fontSize: "10px", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {entry.label}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "12px", color: "#333", mt: 0.25 }}>
                  {entry.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Popover>
      </>
    );
  };

  // Unified Table Component
  const UnifiedTable = ({ scanHistory, historyLoading, handleDelete, handlePreview, formatMonthYear, updateScanStatus, isAdmin, activeTab, handleEditScan, currentEmpId }) => (
    <ThemeProvider theme={Table_theme}>
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {activeTab === 0 && <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Actions</TableCell>}
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Employee Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Month</TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Before Scan</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>After Scan 1</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>After Scan 2</TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Uploaded On</TableCell>
              <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Remarks</TableCell>
              {activeTab === 1 && isAdmin && <TableCell align="center" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Approval</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {historyLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: activeTab === 0 ? 9 : (activeTab === 1 && isAdmin ? 9 : 8) }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : scanHistory?.length > 0 ? (
              scanHistory.map((row) => (
                <TableRow key={row.scan_id} hover>
                  {activeTab === 0 && (
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title={row.status === "Approved" ? "Cannot edit approved scans" : "Edit"}>
                          <span>
                            <IconButton 
                              color="primary" 
                              size="small" 
                              onClick={() => handleEditScan(row)}
                              disabled={row.status === "Approved"}
                              sx={{ color: row.status === "Approved" ? "#ccc" : "#2DB0E4" }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={row.status !== "Uploaded" ? "Can only delete Uploaded scans" : "Delete"}>
                          <span>
                            <IconButton 
                              color="error" 
                              size="small" 
                              onClick={() => handleDelete(row)}
                              disabled={row.status !== "Uploaded"}
                              sx={{ color: row.status !== "Uploaded" ? "#ccc" : "#FF0C0C" }}
                            >
                              <DeleteIcon sx={{ color: row.status !== "Uploaded" ? "#ccc" : "#FF0C0C", width: 17, height: 17 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.employee_name || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMonthYear(row.last_scan_date)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip 
                      label={row.status} 
                      color={row.status === "Approved" ? "success" : row.status === "Rejected" ? "error" : "warning"} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {row.has_before_scan ? (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handlePreview(row.scan_id, "before_scan", "Before Scan")}
                        sx={{ textTransform: "none", fontSize: "12px" }}
                      >
                        View
                      </Button>
                    ) : <Typography variant="body4" color="text.secondary">N/A</Typography>}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {row.has_after_scan1 ? (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handlePreview(row.scan_id, "after_scan1", "After Scan 1")}
                        sx={{ textTransform: "none", fontSize: "12px" }}
                      >
                        View
                      </Button>
                    ) : <Typography variant="body4" color="text.secondary">N/A</Typography>}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {row.has_after_scan2 ? (
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handlePreview(row.scan_id, "after_scan2", "After Scan 2")}
                        sx={{ textTransform: "none", fontSize: "12px" }}
                      >
                        View
                      </Button>
                    ) : <Typography variant="body4" color="text.secondary">N/A</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.uploaded_time}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <RemarksCell row={row} />
                  </TableCell>
                  {activeTab === 1 && isAdmin && (
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {row.status === "Approved" || row.status === "Rejected" ? (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      ) : String(row.employee_id) === String(currentEmpId) ? (
                        <Tooltip title="You cannot approve your own scan">
                          <LockOutlinedIcon sx={{ fontSize: 16, color: "#bbb" }} />
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Button 
                            variant="contained" 
                            color="success" 
                            size="small" 
                            onClick={() => updateScanStatus(row.scan_id, "Approved")}
                            sx={{ fontSize: 10, px: 1 }}
                          >
                            Approve
                          </Button>
                          <Button 
                            variant="contained" 
                            color="error" 
                            size="small" 
                            onClick={() => setRemarksDialog({ open: true, scanId: row.scan_id, remarks: "" })}
                            sx={{ fontSize: 10, px: 1 }}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={activeTab === 0 ? 9 : 8} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">No scan history available</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ThemeProvider>
  );

  // Render file upload section - modern card style
  const renderFileUpload = (type, file, preview, label, required = false) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", display: "block", mb: 0.5 }}>
        {label}{required && <span style={{ color: "#d32f2f" }}> *</span>}
      </Typography>
      <Box
        component="label"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: preview ? "2px solid #2DB0E4" : "2px dashed #ccc",
          borderRadius: "10px",
          height: 120,
          cursor: uploading ? "not-allowed" : "pointer",
          backgroundColor: preview ? "#f0faff" : "#fafafa",
          position: "relative",
          overflow: "hidden",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": { borderColor: "#2DB0E4", backgroundColor: "#f0faff" },
        }}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              style={{ width: "100%", height: "100%", objectFit: "contain", padding: "4px" }}
            />
            {/* Overlay actions */}
            <Box
              sx={{
                position: "absolute",
                bottom: 4,
                right: 4,
                display: "flex",
                gap: 0.5,
                zIndex: 1,
              }}
              onClick={(e) => e.preventDefault()}
            >
              <Tooltip title="Preview full size">
                <IconButton
                  size="small"
                  onClick={(e) => { e.preventDefault(); handlePreview(preview, label, label); }}
                  sx={{ backgroundColor: "rgba(255,255,255,0.9)", width: 24, height: 24, "&:hover": { backgroundColor: "white" } }}
                >
                  <Visibility sx={{ fontSize: 14, color: "#2DB0E4" }} />
                </IconButton>
              </Tooltip>
              {file && (
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.preventDefault(); handleRemoveFile(type); }}
                    sx={{ backgroundColor: "rgba(255,255,255,0.9)", width: 24, height: 24, "&:hover": { backgroundColor: "white" } }}
                  >
                    <Delete sx={{ fontSize: 14, color: "#d32f2f" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </>
        ) : (
          <>
            <CloudUpload sx={{ fontSize: 28, color: "#bbb", mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px", textAlign: "center", px: 1 }}>
              Click to upload
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "10px", color: "#bbb" }}>
              JPG, PNG, GIF, WebP · Max 5MB
            </Typography>
          </>
        )}
        {!uploading && (
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            data-file-type={type}
            onChange={(e) => handleFileChange(e, type)}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ py: 0.5 ,ml:-4}}>
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              // sx={{
              //   "& .MuiTab-root": {
              //     textTransform: "none",
              //     fontSize: "14px",
              //     fontWeight: 500,
              //     minWidth: 150,
              //   },
              //   "& .Mui-selected": {
              //     color: "#2DB0E4",
              //     fontWeight: 600,
              //   },
              // }}
            >
              <Tab label="EMPLOYEE SCAN" />
              {isAdmin && <Tab label="HISTORY & APPROVALS" />}
            </Tabs>
          </Box>

          {/* Top Bar: Add Button, Date Filter, Status Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mb: 2,
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            {/* Add Button - Only on Employee Scan tab */}
            {activeTab === 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  fontWeight: "bold",
                  borderRadius: "8px",
                  px: 2.5,
                  height: 36,
                  backgroundColor: "#2DB0E4",
                  fontSize: "13px",
                  textTransform: "none",
                }}
              >
                Upload Antivirus Scan
              </Button>
            )}

            {/* Date Filter with Clear */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Month"
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM-YYYY"
                  enableAccessibleFieldDOMStructure={false}
                  value={filterMonth}
                  onChange={(newValue) => setFilterMonth(newValue)}
                  slots={{
                    textField: (params) => (
                      <TextField
                        {...params}
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true,
                          value: filterMonth ? filterMonth.format("MMM-YYYY") : "",
                          placeholder: "MMM-YYYY",
                          style: { cursor: "pointer", fontSize: "14px", caretColor: "transparent" },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            height: 36,
                            fontSize: "14px",
                            cursor: "pointer",
                            "& input": { pointerEvents: "none" },
                          },
                          endAdornment: filterMonth ? (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setFilterMonth(null); }}
                              sx={{ height: 24, width: 24, mr: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          ) : params.InputProps?.endAdornment,
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: "160px",
                          backgroundColor: "white",
                          "& .MuiInputLabel-root": { fontSize: "14px" },
                          "& .MuiInputBase-input": { fontSize: "14px" },
                        }}
                      />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Status Filter */}
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                width: 150,
                "& .MuiInputBase-root": {
                  height: 36,
                  backgroundColor: "white",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  fontSize: 12,
                  padding: "0 14px",
                },
                
                "& .MuiInputLabel-root": { fontSize: "14px" },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        minHeight: "32px",
                       
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Uploaded">Uploaded</MenuItem>
              <MenuItem value="Reuploaded">Reuploaded</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </TextField>

            {/* Employee Filter - Only on History & Approval tab */}
            {activeTab === 1 && (
              <TextField
                size="small"
                label="Search Employee"
                
                value={employeeFilter}
                onChange={(e) => setEmployeeFilter(e.target.value)}
                sx={{
                  width: 200,
                  "& .MuiInputBase-root": {
                    height: 36,
                    backgroundColor: "white",
                  },
                  "& .MuiInputLabel-root": { fontSize: "14px" },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#bbb",
                    opacity: 1,
                  },
                }}
              />
            )}
          </Box>

          {/* Upload Dialog */}
          <Dialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
              resetUploadForm();
              setUploadMonth(dayjs());
              setIsEditMode(false);
              setEditingScanId(null);
            }}
            maxWidth="sm"
            fullWidth
            sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
          >
            {/* Header */}
            <DialogTitle
              sx={{
                bgcolor: "#f5f5f5",
                borderRadius: "12px 12px 0 0",
                p: "12px 20px",
                fontSize: "15px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {isRejectedReupload
                ? "Reupload Antivirus Scan"
                : isEditMode
                ? "Edit Antivirus Scan"
                : "Upload Antivirus Scan"}
              <IconButton
                size="small"
                onClick={() => {
                  setUploadDialogOpen(false);
                  resetUploadForm();
                  setUploadMonth(dayjs());
                  setIsEditMode(false);
                  setEditingScanId(null);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: "16px 20px 8px" }}>
              {/* Scan Month */}
              {!isRejectedReupload && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", display: "block", mb: 0.5 }}>
                    Scan Month <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year", "month"]}
                      format="MMM-YYYY"
                      value={uploadMonth}
                      onChange={(newValue) => setUploadMonth(newValue)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: "small",
                          InputLabelProps: { shrink: true },
                          inputProps: { readOnly: true, style: { cursor: "pointer", fontSize: "14px" } },
                          InputProps: {
                            sx: { height: 36, fontSize: "14px", "& input": { pointerEvents: "none" } },
                          },
                          sx: {
                            width: "180px",
                            "& .MuiInputLabel-root": { fontSize: "14px" },
                            "& .MuiInputBase-input": { fontSize: "14px" },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}

              {/* Screenshot Upload Cards */}
              {isRejectedReupload ? (
                /* Rejected reupload: only After Scan 2 */
                <Box sx={{ mb: 2 }}>
                  <Box sx={{
                    p: 2,
                    border: "1px solid #e8e8e8",
                    borderRadius: "10px",
                    backgroundColor: "#fffbf0",
                    mb: 1.5,
                  }}>
                    <Typography variant="caption" sx={{ color: "#e65100", fontWeight: 600, display: "block", mb: 1 }}>
                      ⚠ This scan was rejected. Please upload a new After Scan 2 screenshot.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The original Before Scan and After Scan 1 will be retained.
                    </Typography>
                  </Box>
                  {renderFileUpload("after2", afterScan2File, afterScan2Preview, "After Scan 2", true)}
                </Box>
              ) : (
                /* Normal upload/edit: Before + After Scan 1 side by side */
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  {renderFileUpload("before", beforeScanFile, beforeScanPreview, "Before Scan", true)}
                  {renderFileUpload("after", afterScanFile, afterScanPreview, "After Scan 1", true)}
                </Box>
              )}

              {/* Remarks */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#555", display: "block", mb: 0.5 }}>
                  Remarks <span style={{ color: "#aaa", fontWeight: 400 }}>(Optional)</span>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Add any remarks..."
                  disabled={uploading}
                  value={uploadRemarks}
                  onChange={(e) => setUploadRemarks(e.target.value)}
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px" },
                  }}
                />
              </Box>

              {uploading && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Uploading...</Typography>
                  <LinearProgress sx={{ mt: 0.5, borderRadius: 4 }} />
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setUploadDialogOpen(false);
                  resetUploadForm();
                  setUploadMonth(dayjs());
                  setIsEditMode(false);
                  setEditingScanId(null);
                }}
                sx={{ borderRadius: "8px", textTransform: "none", color: "#555", borderColor: "#ccc", minWidth: 80 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit}
                disabled={!canUpload || uploading}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: "#2DB0E4",
                  minWidth: 100,
                  fontWeight: 600,
                }}
              >
                {uploading
                  ? isEditMode ? "Updating..." : "Uploading..."
                  : isRejectedReupload ? "Reupload"
                  : isEditMode ? "Update"
                  : "Submit"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Unified Table */}
          <Box>
            <UnifiedTable
              scanHistory={scanHistory}
              historyLoading={historyLoading}
              handleDelete={handleDelete}
              handlePreview={handlePreview}
              formatMonthYear={formatMonthYear}
              updateScanStatus={updateScanStatus}
              isAdmin={isAdmin}
              activeTab={activeTab}
              handleEditScan={handleEditScan}
              currentEmpId={empId}
            />
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Box>
          </Box>

          {/* Delete Confirmation Dialog */}
          {getConfirmDeleteDialog({
            open: confirmOpen,
            onClose: () => {
              setConfirmOpen(false);
              setDeleteId(null);
              setSelectedScan(null);
            },
            onConfirm: confirmDelete,
            title: "Confirm Deletion",
            contentText: selectedScan
              ? `Are you sure you want to permanently delete the antivirus scan for ${
                  selectedScan.employee_name || "this employee"
                } uploaded on ${selectedScan.uploaded_time || ""}?`
              : "Are you sure you want to permanently delete this scan?",
          })}

          {/* Remarks Dialog (for rejection input) */}
          <Dialog
            open={remarksDialog.open}
            onClose={() => setRemarksDialog({ open: false, scanId: null, remarks: "" })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Reject Scan</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                multiline
                rows={3}
                fullWidth
                value={remarksDialog.remarks}
                onChange={(e) =>
                  setRemarksDialog((prev) => ({
                    ...prev,
                    remarks: e.target.value,
                  }))
                }
                placeholder="Enter rejection remarks..."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRemarksDialog({ open: false, scanId: null, remarks: "" })}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() =>
                  sendStatusUpdate(remarksDialog.scanId, "Rejected", remarksDialog.remarks)
                }
              >
                Reject
              </Button>
            </DialogActions>
          </Dialog>

          {/* Image Preview Dialog */}
          <Dialog
            open={previewDialog.open}
            onClose={() => {
              setPreviewDialog({ open: false, image: null, title: "" });
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>{previewDialog.title}</DialogTitle>
            <DialogContent>
              {previewDialog.image ? (
                <img
                  src={previewDialog.image}
                  alt={previewDialog.title}
                  style={{ width: "100%", height: "auto" }}
                />
              ) : (
                <Typography>No preview available</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setPreviewDialog({ open: false, image: null, title: "" });
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Snackbar */}
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess("")}
          >
            <Alert onClose={() => setSuccess("")} severity="success">
              {success}
            </Alert>
          </Snackbar>

          {/* Error Snackbar */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError("")}
          >
            <Alert onClose={() => setError("")} severity="error">
              {error}
            </Alert>
          </Snackbar>
        </ThemeProvider>
      </Container>
    </MainLayout>
  );
};

export default Antivirus;
